'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */

import { useApplyV3Modal } from '@hyphengroup/cms-components/components/ApplyCta';
import { Modal } from '@hyphengroup/cms-components/components/Modal';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useCallback, useEffect, useState } from 'react';
import { ApplyStaticText } from '../i18n';
import { ApplyViewMode } from '../lib/types';
import { ApplyForm } from './ApplyForm';
import type { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import Button from '$/components/Button';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; product: CmsApplyRewardProduct }
  | { status: 'error' };

export const ApplyV3Modal: React.FC<{ isPreview?: boolean }> = ({
  isPreview = false,
}) => {
  const { isOpen, rewardProductId, mode, loan, close } = useApplyV3Modal();
  const context = useClientContext();
  const staticText = getClientTranslations(ApplyStaticText, context);
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'idle' });
  const [initialNow, setInitialNow] = useState<number | null>(null);

  const loadProduct = useCallback(
    async (id: number) => {
      setFetchState({ status: 'loading' });
      try {
        const product = await feApiFrontClient.getApplyProduct({
          rewardProductId: id,
          isDraft: isPreview,
        });
        if (!product) {
          setFetchState({ status: 'error' });
          return;
        }
        setFetchState({ status: 'success', product });
      } catch {
        setFetchState({ status: 'error' });
      }
    },
    [isPreview],
  );

  useEffect(() => {
    if (!isOpen || rewardProductId == null) {
      setFetchState({ status: 'idle' });
      setInitialNow(null);
      return;
    }
    setInitialNow(Date.now());
    loadProduct(rewardProductId);
  }, [isOpen, rewardProductId, loadProduct]);

  if (!isOpen || rewardProductId == null || mode == null) {
    return null;
  }

  return (
    <Modal
      isOpen
      onClose={close}
      hideCloseButton
      sheetOnMobile
      // Test hook (CMS-1878) — forwarded onto the Dialog root by Modal.
      data-component="apply-v3-modal"
      // The mobile sheet has a fixed height: grown to fit, its top edge would
      // jump when the product arrives and again on the bundle step (CLS).
      className="flex h-[92svh] flex-col md:block md:h-auto md:!w-[--general-6-col-width] md:!max-w-[--general-6-col-width]"
    >
      {fetchState.status === 'loading' || fetchState.status === 'idle' ? (
        <div className="flex min-h-[50vh] flex-1 items-center justify-center p-spacing/padding/section-y">
          <MaterialIcon
            icon="sync"
            px={48}
            className="animate-spin text-text/secondary"
          />
        </div>
      ) : fetchState.status === 'error' ? (
        <div className="flex min-h-[40vh] flex-1 flex-col items-center justify-center gap-spacing/gap/y-sm p-spacing/padding/section-y text-center">
          <MaterialIcon
            icon="warning"
            px={48}
            className="text-text/secondary"
          />
          <h2 className="mhg-alpha-typography-display-1 text-text/primary">
            {staticText.applyModalErrorTitle}
          </h2>
          <p className="mhg-alpha-typography-body-1-relaxed text-text/secondary">
            {staticText.applyModalErrorSubtitle}
          </p>
          <Button styleType="cta" onClick={() => loadProduct(rewardProductId)}>
            {staticText.applyModalTryAgain}
          </Button>
        </div>
      ) : initialNow != null ? (
        <ApplyForm
          mode={mode}
          viewMode={ApplyViewMode.Modal}
          product={fetchState.product}
          initialNow={initialNow}
          loan={loan}
          onClose={close}
          isDraft={isPreview}
        />
      ) : null}
    </Modal>
  );
};
